import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, RotateCcw, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getAllActes } from '../../api/cotations';

export default function StepConsultation({ data, finalize, currentPage }) {
  const navigate = useNavigate();
  const [allActes, setAllActes] = useState(null);
  const [erreur, setErreur] = useState(null);

  // SNapshot du parcours de cotation
  const [parcours, setParcours] = useState(() => data?.parcours ?? []);

  // cases cochées pour les étapes multiselect
  const [cochees, setCochees] = useState([]);

  useEffect(() => {
    let annule = false;

    getAllActes()
      .then((res) => {
        if (!annule) setAllActes(res);
      })
      .catch((e) => {
        if (!annule) setErreur(e.message);
      });

    return () => {
      annule = true;
    };
  }, []);

  const etapes = allActes?.etapes;
  const idCourant = parcours.length ? parcours[parcours.length - 1].next : 'start';
  const etape = etapes?.[idCourant];
  const estMulti = etape?.type_ui === 'multiselect';
  const estTermine = idCourant === 'end';

  // tous les codes choisis, dans l'ordre
  const chosenCodes = useMemo(() => parcours.flatMap((p) => p.codes), [parcours]);

  const pills = useMemo(() => {
    const compteur = new Map();
    chosenCodes.forEach((c) => compteur.set(c, (compteur.get(c) || 0) + 1));
    return [...compteur].map(([code, count]) => ({ code, count }));
  }, [chosenCodes]);

  const actes = useMemo(() => {
    const groupes = [];
    let courant = [];

    parcours.forEach((p) => {
      if (p.next === 'start') {
        if (courant.length) groupes.push(courant);
        courant = [];
      } else if (p.next !== 'end') {
        courant.push(p);
      }
    });

    if (courant.length) groupes.push(courant);
    return groupes;
  }, [parcours]);

  // Retire une seule occurrence du code ciblé dans le parcours sans supprimer l'étape
  const supprimerCode = (codeToRemove) => {
    setParcours((prevParcours) => {
      let removed = false;
      return prevParcours.map((p) => {
        if (removed || !p.codes.includes(codeToRemove)) return p;

        // Retire uniquement la première occurrence trouvée
        const index = p.codes.indexOf(codeToRemove);
        const newCodes = [...p.codes];
        newCodes.splice(index, 1);
        removed = true;

        return {
          ...p,
          codes: newCodes,
        };
      });
    });
  };

  const avancer = (options, next) => {
    if (!next || !etapes?.[next]) {
      console.warn(`Étape suivante introuvable depuis "${idCourant}" :`, next);
      return;
    }

    setParcours((p) => [
      ...p,
      {
        etape: idCourant,
        question: etape?.question,
        labels: options.map((o) => o.label),
        codes: options.flatMap((o) => o.add_codes ?? []),
        next,
      },
    ]);
    setCochees([]);
  };

  const choisir = (option) => avancer([option], option.next_step);

  const valider = () => {
    const options = etape.choix.filter((o) => cochees.includes(o.label));

    const next = options.find((o) => o.next_step)?.next_step ?? etape.next_step;

    avancer(options, next);
  };

  const basculer = (label) => {
    setCochees((c) => (c.includes(label) ? c.filter((l) => l !== label) : [...c, label]));
  };

  const retour = () => {
    setParcours((p) => p.slice(0, -1));
    setCochees([]);
  };

  const recommencer = () => {
    setParcours([]);
    setCochees([]);
  };

  if (erreur) {
    return <p className="text-sm text-red-700">Impossible de charger les actes : {erreur}</p>;
  }

  return (
    <section className="space-y-6">
      <div className="space-y-2">
        <h2 className="text-lg font-medium">Codes sélectionnés</h2>

        <div className="flex flex-wrap gap-2">
          {pills.map(({ code, count }) => (
            <span
              key={code}
              className="rounded-full bg-red-100 px-3 py-1 text-sm font-medium text-red-700"
            >
              {code}
              {count > 1 && <span className="ml-1 font-semibold">x{count}</span>}
            </span>
          ))}

          {pills.length === 0 && (
            <span className="text-sm text-slate-500">Aucun code sélectionné.</span>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-medium">
            {estTermine ? 'Récapitulatif' : (etape?.question ?? 'Chargement...')}
          </h2>

          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={retour}
              disabled={parcours.length === 0}
              className="inline-flex items-center gap-1.5 rounded border border-slate-300 bg-slate-100 px-3 py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-200 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-slate-100"
            >
              <ArrowLeft size={16} />
              Précédent
            </button>

            <button
              type="button"
              onClick={recommencer}
              disabled={parcours.length === 0}
              className="inline-flex items-center gap-1.5 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-800 transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-amber-50"
            >
              <RotateCcw size={16} />
              Recommencer
            </button>
          </div>
        </div>

        {estTermine ? (
          <div className="space-y-4">
            {actes.map((acte, i) => (
              <div key={i} className="rounded border border-slate-200 p-4">
                {actes.length > 1 && (
                  <p className="mb-2 text-sm font-semibold text-slate-700">Acte {i + 1}</p>
                )}

                <ul className="space-y-3">
                  {acte.map((p, j) => (
                    <li key={j} className="flex items-start justify-between gap-3 text-sm">
                      <div>
                        {p.question && <p className="text-xs text-slate-500">{p.question}</p>}
                        <p className="font-medium">
                          {p.labels.length ? p.labels.join(', ') : 'Aucune sélection'}
                        </p>
                      </div>

                      {p.codes.length > 0 && (
                        <div className="flex shrink-0 flex-wrap justify-end gap-1">
                          {p.codes.map((code, k) => (
                            <span
                              key={k}
                              className="rounded-full bg-red-100 px-2 py-0.5 font-mono text-xs text-red-700"
                            >
                              {code}
                              <button
                                type="button"
                                onClick={() => supprimerCode(code)}
                                className="ml-1 rounded-full hover:bg-red-200 transition text-red-700"
                                title={`Supprimer ${code}`}
                              >
                                <X size={12} />
                              </button>
                            </span>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            {currentPage === 'sortie' ? (
              <button
                type="button"
                className="w-full rounded-lg bg-red-600 py-4 text-base font-semibold text-white shadow-md transition hover:bg-red-700"
                onClick={() => finalize({ parcours, codes: chosenCodes, redirectSortie: false })}
              >
                Valider la cotation
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="w-full rounded-lg bg-red-600 py-4 text-base font-semibold text-white shadow-md transition hover:bg-red-700"
                  onClick={async () => {
                    await finalize({ parcours, codes: chosenCodes, redirectSortie: true });
                    navigate('/passages');
                  }}
                >
                  Valider et mettre en sortie
                </button>

                <button
                  type="button"
                  className="w-full rounded border border-slate-300 bg-white py-2 text-sm font-medium text-slate-700 transition hover:bg-slate-50"
                  onClick={() => finalize({ parcours, codes: chosenCodes, redirectSortie: false })}
                >
                  Valider et revenir à la fiche patient
                </button>
              </>
            )}
          </div>
        ) : estMulti ? (
          <>
            <div className="space-y-3">
              {etape.choix.map((option) => (
                <label
                  key={option.label}
                  className="flex cursor-pointer items-start gap-3 rounded border border-slate-200 p-3 hover:bg-slate-50"
                >
                  <input
                    type="checkbox"
                    checked={cochees.includes(option.label)}
                    onChange={() => basculer(option.label)}
                    className="mt-1"
                  />
                  <span>{option.label}</span>
                </label>
              ))}
            </div>

            <button
              type="button"
              onClick={valider}
              className="w-full rounded bg-red-600 py-3 text-white transition hover:bg-red-700"
            >
              Valider
            </button>
          </>
        ) : (
          <div className="grid grid-cols-1 gap-3">
            {etape?.choix.map((option) => (
              <button
                key={option.label}
                type="button"
                onClick={() => choisir(option)}
                className="w-full rounded bg-red-600 py-3 text-white transition hover:bg-red-700"
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}